import {array, Callback, Mix} from "../Core/Reflection";
import {ItemType, PackageModel} from "../Data/ItemModel";
import {playVO, PostcardVO, ReactionVO} from "../Data/VoiceOver";

import {Application} from "../Application";
import {ButtonPulse} from "./Button";
import {Container} from "../Core/Container";
import {Ease} from "../Core/Animatable";
import {Poly} from "../Core/Shape";
import {Postcard} from "./Postcard";
import {Sprite} from "../Core/Sprite";
import {TransformBackup} from "../Core/TransformBackup";
import {textureOrDefault} from "../Core/AssetHelpers";
import {Times} from "../Core/Signal";

export class Package extends Mix(ButtonPulse, TransformBackup) {

    public readonly background = this.visuals.addChild(new Container());
    public readonly sprites = array(() => new Sprite());
    public readonly tape = new Sprite();
    public readonly flap = new Sprite();
    public readonly stamp = this.visuals.addChild(new Sprite());
    public readonly postcard = new Postcard();
    public readonly puzzlePieces = array(() => new PuzzlePiece());
    public readonly boxItemMask: number[] = [];
    public readonly itemInBox = {x: 0, y: 0, width: 0, height: 0};
    public readonly itemAboveBox = {x: 0, y: 0, width: 0, height: 0};

    private model?: PackageModel;

    onLayout() {
        this.clickSfx = undefined;
        this.background.addChild(this.sprites[0]);
        this.stamp.visible = false;
        this.postcard.saveTransform();
    }

    setModel(model: PackageModel) {
        this.model = model;

        this.stamp.visible = true;
        this.stamp.texture = textureOrDefault(`package-stamp-${model.country}-${model.itemType}`, `package-stamp-${model.country}`);
        this.stamp.scale.set(2);

        const textureName = model.itemType === ItemType.Robot ? "package-metal-" : "package-";
        this.sprites.forEach((it, i) => it.texture = textureOrDefault(`${textureName}${i + 1}`));
        this.flap.texture = textureOrDefault(`${textureName}4-flap`);

        this.postcard.setModel(model);
    }

    getModel() {
        return this.model;
    }

    open(): boolean {
        const n = this.sprites.length - 1;
        this.sprites.forEach(it => it.removeFromParent());
        this.background.addChild(this.sprites[n]);
        this.visuals.addChild(this.tape);
        this.visuals.addChild(this.flap);
        return true;
    }

    showPostcard(cb?: Callback) {
        let onComplete = () => {
            if (this.model) {
                playVO([
                    ...ReactionVO(),
                    ...(PostcardVO(this.model) ?? []),
                    0.5,
                    "LetsGoLuna_021"
                ], cb);
            } else if (cb) {
                cb();
            }
        };
        if (this.model && this.model.itemType === ItemType.Puzzle) {
            onComplete = Times(this.puzzlePieces.length, onComplete);
            let d = 0;
            for (const piece of this.puzzlePieces) {
                this.showContent(piece, d, onComplete);
                d += 0.2;
            }
        } else {
            this.showContent(this.postcard, 0, onComplete);
        }
        this.tape.alphaTo(0, 0.2, () => {
            this.tape.alpha = 1;
            this.tape.removeFromParent();
        });
    }

    hidePostcard() {
        if (this.model && this.model.itemType === ItemType.Puzzle) {
            let d = 0;
            for (const piece of this.puzzlePieces) {
                this.hideContent(piece, d);
                d += 0.1;
            }
        } else {
            this.hideContent(this.postcard);
        }
    }

    private showContent(content: Container & typeof TransformBackup, delay = 0, cb?: Callback) {
        this.addChild(content);
        const t = content.getTransformBackup();
        content.mask = this.addChild(new Poly(this.boxItemMask));
        content.setBounds(this.itemInBox);
        content.alignBy(this.itemInBox, 0.5, 0.5);
        content.scaleTo(t.scaleX, 0.9, Ease.backOut, {delay: 0.1 + delay});
        content.moveXYTo(t.x, t.y, 1, Ease.backOut, {
            delay: delay,
            onComplete: () => {
                (content.mask as Poly).destroy();
                content.mask = null;
                if (cb) {
                    cb();
                }
            }
        });
    }

    private hideContent(content: Container & typeof TransformBackup, delay = 0, cb?: Callback) {
        const y = content.y;
        content.moveYTo(-Application.instance.size.y - this.y - content.height * 0.5, 0.4, Ease.sineIn, {
            delay: delay, onComplete: () => {
                content.y = y;
                content.removeFromParent();
            }
        });
    }

    takeItem(item: Container & typeof TransformBackup, cb?: Callback) {

        item.changeParent(this.visuals);
        item.saveTransform();
        const end = item.getTransformBackup(true);

        item.setBounds(this.itemAboveBox);
        item.alignBy(this.itemAboveBox, 0.5, 0.5);
        item.saveTransform();
        const middle = item.getTransformBackup(true);

        item.setBounds(this.itemInBox);
        item.alignBy(this.itemInBox, 0.5, 0.5);
        item.mask = this.addChild(new Poly(this.boxItemMask));
        item.zIndex = this.tape.zIndex;

        const t = 2;
        item.tweenTo(t * 0.4, Ease.sineIn, {
            ...middle,
            onComplete: () => {
                item.zIndex = -1;
                (item.mask as Poly).destroy();
                item.mask = null;
                item.tweenTo(t * 0.6, Ease.sineOut, {
                    ...end,
                    onComplete: () => {
                        item.changeParent(this);
                        this.visuals.alphaTo(0, 0.2, () => {
                            this.visuals.alpha = 1;
                            this.tape.removeFromParent();
                            this.flap.removeFromParent();
                            this.sprites.forEach(it => it.removeFromParent());
                            this.background.addChild(this.sprites[0]);
                            this.visible = false;
                            this.enabled = true;
                            if (cb) cb();
                        });
                    }
                });
            }
        });
    }
}

class PuzzlePiece extends Mix(Sprite, TransformBackup) {
}